import type { KeyboardEvent } from 'react';

import { pick } from 'ramda';

import { pickAttrs } from '../../../../utils/pickAttrs';
import { combineEventHandlers } from '../../../Floating/Floating.utils';
import { SELECT_TRIGGER_EVENTS } from '../../Select.constants';
import { useSelectRootContext } from '../root/SelectRootContext';
import { useSelectRootFloatingContext } from '../root/SelectRootFloatingContext';

import type { SelectTriggerProps } from './SelectTrigger';

type Config = {
    hasCustomInput: boolean;
};

/**
 * Floating UI’s click/keyboard interaction adds an `onKeyDown` handler to the trigger element.
 * That handler interprets the <Space> key as “open the menu” and calls `preventDefault()`.
 *
 * When we render an editable custom input inside the trigger (autoComplete mode), the space
 * key never reaches the input element because the handler runs first. We wrap the handler here
 * so that, in that specific scenario, the inline input consumes the space character while the
 * dropdown still opens. Non-editable triggers retain the default behaviour.
 *
 * @param props          Trigger props passed down from the public Select API.
 * @param hasCustomInput Whether the Select is rendering a custom inline input (autoComplete mode).
 */

export const useSelectTriggerProps = (props: SelectTriggerProps, { hasCustomInput }: Config) => {
    const { floatingInteractions } = useSelectRootFloatingContext();
    const { canDropdownOpen, autoComplete } = useSelectRootContext();

    const triggerEvents = canDropdownOpen
        ? pick(SELECT_TRIGGER_EVENTS, floatingInteractions.getReferenceProps())
        : {};

    if (hasCustomInput && autoComplete && typeof triggerEvents.onKeyDown === 'function') {
        const originalOnKeyDown = triggerEvents.onKeyDown;
        triggerEvents.onKeyDown = ((event: KeyboardEvent) => {
            const tagName = (event.target as HTMLElement | null)?.tagName?.toLowerCase();
            if (
                tagName === 'input' &&
                (event.key === ' ' || event.key === 'Spacebar' || event.key === 'Space')
            ) {
                return;
            }
            originalOnKeyDown(event);
        }) as typeof originalOnKeyDown;
    }

    const dataAttrs = pickAttrs(props, {
        data: true
    });

    const propsEvents = pickAttrs(props, {
        events: true
    });

    const name = props.id || props['aria-label'];

    const filteredProps = {
        ...combineEventHandlers(triggerEvents, propsEvents),
        ...dataAttrs,
        ...(name && { name })
    };

    return filteredProps;
};
